import { useMemo } from 'react';
import useAuthStore from '../store/authStore';
import { ROLES } from '../constants/roles';
import { PERMISSIONS } from '../constants/permissions';

// Re-exportar para compatibilidad
export { ROLES } from '../constants/roles';
export { PERMISSIONS } from '../constants/permissions';

/**
 * Hook personalizado para manejar permisos de usuario
 */
export const usePermissions = () => {
  const { user, isAuthenticated } = useAuthStore();

  const userRole = useMemo(() => {
    if (!user || !user.rol_id) {
      console.log('usePermissions: Usuario sin rol válido:', user);
      return null;
    }

    return user.rol_id;
  }, [user]);

  const roleName = useMemo(() => {
    if (!user || !user.rol) return null;
    return user.rol.nombre;
  }, [user]);
  /**
   * Verifica si el usuario tiene un permiso específico
   * @param {string} permission - Nombre del permiso a verificar
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!isAuthenticated || !userRole) return false;
    
    const permissionConfig = PERMISSIONS[permission];
    if (!permissionConfig) {
      console.warn(`Permiso '${permission}' no existe`);
      return false;
    }
    
    // Soportar tanto el formato nuevo como el legacy
    const allowedRoles = permissionConfig.roles || permissionConfig;
    
    return allowedRoles.includes(userRole);
  };

  /**
   * Verifica si el usuario tiene alguno de los roles especificados
   * @param {Array} roles - Array de IDs de roles o nombres
   * @returns {boolean}
   */
  const hasAnyRole = (roles) => {
    if (!isAuthenticated || !userRole) return false;
    
    return roles.some(role => {
      if (typeof role === 'number') {
        return userRole === role;
      }
      if (typeof role === 'string') {
        return roleName === role;
      }
      return false;
    });
  };

  /**
   * Verifica si el usuario es administrador
   * @returns {boolean}
   */
  const isAdmin = () => {
    return userRole === ROLES.ADMINISTRADOR;
  };

  /**
   * Verifica si el usuario es administrativo
   * @returns {boolean}
   */
  const isAdministrativo = () => {
    return userRole === ROLES.ADMINISTRATIVO;
  };

  /**
   * Verifica si el usuario es auditor
   * @returns {boolean}
   */
  const isAuditor = () => {
    return userRole === ROLES.AUDITOR;
  };

  /**
   * Verifica si el usuario es coordinador
   * @returns {boolean}
   */
  const isCoordinador = () => {
    return userRole === ROLES.COORDINADOR;
  };

  /**
   * Verifica si el usuario puede gestionar empleados y novedades
   * @returns {boolean}
   */
  const canManageData = () => {
    return hasAnyRole([ROLES.ADMINISTRADOR, ROLES.ADMINISTRATIVO, ROLES.COORDINADOR]);
  };

  /**
   * Verifica si el usuario solo tiene permisos de lectura
   * @returns {boolean}
   */
  const isReadOnly = () => {
    return userRole === ROLES.AUDITOR;
  };

  return {
    userRole,
    roleName,
    hasPermission,
    hasAnyRole,
    isAdmin,
    isAdministrativo,
    isAuditor,
    isCoordinador,
    canManageData,
    isReadOnly,
    isAuthenticated
  };
};

export default usePermissions;
